import { EOnPremModelType, ESearchServiceModel, ICommonSettings, ISearchServiceSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigSearchServiceSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ISearchServiceSettings;
}

export interface IConfigSearchServiceSettingsProps {
  parentConfigValue?: string | null;
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingSearchServiceId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSearchServiceSettings extends React.PureComponent<
  IConfigSearchServiceSettingsProps,
  IConfigSearchServiceSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSearchServiceSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSearchServiceSettingsProps & IWithSDProps) {
    if (prevProps.editingSearchServiceId !== this.props.editingSearchServiceId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingSearchServiceId) return;

    const editingConfig = this.props.commonSettings.searchServiceSettings?.find(
      (x) => x.id === this.props.editingSearchServiceId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const searchServiceSettings = this.props.commonSettings?.searchServiceSettings;
    if (!searchServiceSettings) return;

    const isSettingAlreadyExist =
      searchServiceSettings.some((x) => x.name === value && x.id !== this.props.editingSearchServiceId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("Search Service Settings Name. is already exist.");
  };

  integerValidator = async (_rule, value, itemName: string) => {
    if (!value) return;
    const val = Number(value);
    if (!Number.isInteger(val)) throw new Error(`${itemName} Port value should be an integer.`);
    if (val < 0) throw new Error(`${itemName} Port value should be positive.`);
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ISearchServiceSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      baseUrl: data.baseUrl?.trim(),
      qdrantServicePort: data.qdrantServicePort,
      pythonServicePort: data.pythonServicePort,
      qdrantStoragePath: data.qdrantStoragePath,
      modelType: data.modelType,
      onPremModelType: data.onPremModelType
    };
    if (this.props.editingSearchServiceId) {
      obj.id = this.props.editingSearchServiceId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "searchServiceSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "searchServiceSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingSearchServiceId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSearchServiceId || "",
      configKey: "searchServiceSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Search Service setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Search Service setting"
        secondHeader="Search Service setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSearchServiceId ? this.deleteSetting : undefined}
            deleteTestId="searchServiceSettingDeleteButton"
            submitTestId="searchServiceSettingCreateButton"
            submitLabel={this.props.editingSearchServiceId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSearchServiceForm"
          />
        }
      >
        <Form
          id="configSearchServiceForm"
          name="searchServiceSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_searchservicepop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_searchservicepop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Search Service Settings Name." },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Search Service Setiings Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="searchServiceSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Base Url" tooltipContentKey="appsettings_searchservicepop_up_baseurl">
              <Form.Item name="baseUrl">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Vector Database Service Port"
              tooltipContentKey="appsettings_searchservicepop_up_vectordatabaseserviceport"
            >
              <Form.Item
                name="qdrantServicePort"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Success") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="6333" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Python Service Port"
              tooltipContentKey="appsettings_searchservicepop_up_pythonserviceport"
            >
              <Form.Item
                name="pythonServicePort"
                rules={[{ validator: (rule, value) => this.integerValidator(rule, value, "Info") }]}
              >
                <KuikaInput size="md" type="number" min={0} placeholder="5000" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Storage Path" tooltipContentKey="appsettings_searchservicepop_up_storagepath">
              <Form.Item name="qdrantStoragePath">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Model Type" required tooltipContentKey="appsettings_searchservicepop_up_modeltype">
              <Form.Item
                name="modelType"
                initialValue={ESearchServiceModel.OpenAI}
                rules={[{ required: true, message: "Please select a Search Service Settings Model Type." }]}
              >
                <KuikaSelectbox
                  size="md"
                  placeholder="Select a Model Type"
                  options={[
                    { value: ESearchServiceModel.OpenAI, label: "OpenAI" },
                    { value: ESearchServiceModel.Onprem, label: "Onprem" }
                  ]}
                />
              </Form.Item>
            </ConfigField>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) =>
                getFieldValue("modelType") === ESearchServiceModel.Onprem && (
                  <ConfigField
                    label="Onprem Model Type"
                    required
                    tooltipContentKey="appsettings_searchservicesettingpop_up_onpremmodeltype"
                  >
                    <Form.Item
                      name="onPremModelType"
                      initialValue={EOnPremModelType.Qwen25}
                      rules={[
                        { required: true, message: "Please select a Search Service Settings On Prem Model Type." }
                      ]}
                    >
                      <KuikaSelectbox
                        size="md"
                        placeholder="Select a Model Type"
                        options={[
                          { value: EOnPremModelType.Qwen25, label: "Qwen2.5" },
                          { value: EOnPremModelType.Mistral, label: "Mistral" },
                          { value: EOnPremModelType.Deepseek, label: "Deepseek" },
                          { value: EOnPremModelType.Llama, label: "Llama" }
                        ]}
                      />
                    </Form.Item>
                  </ConfigField>
                )
              }
            </Form.Item>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
